import { AlertCircle, Clock, Package2, RefreshCw } from 'lucide-react'
import React, { useEffect } from 'react'

import {
  useDevanTaskActions,
  useDevanTaskState
} from '../customs/stores/useDevanTaskStore'

import DevanTaskCard from './components/DevanTaskCard'

import { MasterBillStatus } from '@/types/customs'

const DevanTaskList: React.FC = () => {
  const { loading, error, devanTasks, refreshing } = useDevanTaskState()
  const { fetchDevanTasks, clearError, refresh } =
    useDevanTaskActions()

  useEffect(() => {
    fetchDevanTasks()
  }, [])

  const handleRefresh = async () => {
    await refresh()
  }

  // 统计数据
  const awaitingCount = devanTasks.filter(
    (task) => task.status === MasterBillStatus.AWAITING_DEVAN
  ).length
  const devanningCount = devanTasks.filter(
    (task) => task.status === MasterBillStatus.DEVANNING
  ).length

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-2 text-gray-500">
          <RefreshCw className="h-6 w-6 animate-spin" />
          <span>加载拆柜任务中...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 页面头部 */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">拆柜任务管理</h2>
          <p className="mt-1 text-sm text-gray-600">管理待拆柜和拆柜中的任务</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          <RefreshCw
            className={`h-4 w-4 mr-2 ${refreshing ? 'animate-spin' : ''}`}
          />
          刷新
        </button>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-8 w-8 text-yellow-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    待拆柜任务
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {awaitingCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package2 className="h-8 w-8 text-orange-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    拆柜中任务
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {devanningCount}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Package2 className="h-8 w-8 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    总任务数
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {devanTasks.length}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 错误提示 */}
      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <AlertCircle className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">操作失败</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <div className="-mx-2 -my-1.5 flex">
                  <button
                    type="button"
                    onClick={clearError}
                    className="rounded-md bg-red-50 px-2 py-1.5 text-sm font-medium text-red-800 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-red-50 focus:ring-red-600"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 任务列表 */}
      {devanTasks.length === 0 ? (
        <div className="text-center py-12">
          <Package2 className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">
            暂无拆柜任务
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            当前没有待拆柜或拆柜中的任务
          </p>
          <div className="mt-6">
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              重新加载
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {devanTasks.map((task) => (
            <DevanTaskCard key={task.id} masterBill={task} />
          ))}
        </div>
      )}
    </div>
  )
}

export default DevanTaskList
